import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  useToast,
} from '@org/ui';
import { Pin, GripVertical, Trash2, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export const Route = createFileRoute('/admin/default-pins')({
  component: AdminDefaultPins,
});

interface DefaultPin {
  defaultPinnedSpaceId: string;
  position: number;
  spaceId: string;
  name: string;
  slug: string;
  description: string | null;
}

function SortablePinItem({
  pin,
  onRemove,
  isRemoving,
}: {
  pin: DefaultPin;
  onRemove: (spaceId: string) => void;
  isRemoving: boolean;
}) {
  const { t } = useTranslation('community');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pin.spaceId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 rounded-lg border border-border bg-card p-3 ${
        isDragging ? 'z-10 shadow-lg opacity-90' : ''
      }`}
    >
      <button
        type="button"
        className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
        aria-label={t('defaultPins.dragHandle')}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5" aria-hidden="true" />
      </button>

      <div className="min-w-0 flex-1">
        <p className="font-medium">{pin.name}</p>
        {pin.description && (
          <p className="truncate text-sm text-muted-foreground">
            {pin.description}
          </p>
        )}
      </div>

      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        #{pin.position}
      </span>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => onRemove(pin.spaceId)}
        disabled={isRemoving}
        aria-label={t('defaultPins.removeSpace')}
        className="shrink-0 text-destructive hover:text-destructive"
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

function AdminDefaultPins() {
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const [addOpen, setAddOpen] = useState(false);

  const utils = communityTrpc.useUtils();

  const { data, isLoading } = communityTrpc.admin.defaultPins.list.useQuery();
  const { data: spacesData } = communityTrpc.spaces.list.useQuery();

  const setMutation = communityTrpc.admin.defaultPins.set.useMutation({
    onSuccess: () => {
      utils.admin.defaultPins.list.invalidate();
      toast({ title: t('defaultPins.reorderSuccess') });
    },
  });

  const addMutation = communityTrpc.admin.defaultPins.add.useMutation({
    onSuccess: (result) => {
      utils.admin.defaultPins.list.invalidate();
      if (result.alreadyDefault) {
        toast({ title: t('defaultPins.alreadyPinned') });
      } else {
        toast({ title: t('defaultPins.addSuccess') });
      }
      setAddOpen(false);
    },
  });

  const removeMutation = communityTrpc.admin.defaultPins.remove.useMutation({
    onSuccess: () => {
      utils.admin.defaultPins.list.invalidate();
      toast({ title: t('defaultPins.removeSuccess') });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const pins = data?.defaultPins ?? [];
  const pinnedSpaceIds = new Set(pins.map((p) => p.spaceId));
  const availableSpaces = (spacesData?.spaces ?? []).filter(
    (s) => !pinnedSpaceIds.has(s.spaceId)
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = pins.findIndex((p) => p.spaceId === active.id);
    const newIndex = pins.findIndex((p) => p.spaceId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(pins, oldIndex, newIndex);
    const orderedSpaceIds = reordered.map((p) => p.spaceId);

    utils.admin.defaultPins.list.setData(undefined, {
      defaultPins: reordered.map((p, i) => ({ ...p, position: i + 1 })),
    });

    setMutation.mutate({ orderedSpaceIds });
  }

  function handleRemove(spaceId: string) {
    removeMutation.mutate({ spaceId });
  }

  function handleAdd(spaceId: string) {
    addMutation.mutate({ spaceId });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('defaultPins.title')}</h1>
          <p className="text-muted-foreground">
            {t('defaultPins.description')}
          </p>
        </div>

        <Popover open={addOpen} onOpenChange={setAddOpen}>
          <PopoverTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('defaultPins.addSpace')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="end">
            <Command>
              <CommandInput placeholder={t('defaultPins.searchSpaces')} />
              <CommandList>
                <CommandEmpty>{t('defaultPins.noSpacesFound')}</CommandEmpty>
                <CommandGroup>
                  {availableSpaces.map((space) => (
                    <CommandItem
                      key={space.spaceId}
                      value={space.name}
                      onSelect={() => handleAdd(space.spaceId)}
                      className="cursor-pointer"
                    >
                      <Search className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{space.name}</p>
                        {space.description && (
                          <p className="truncate text-xs text-muted-foreground">
                            {space.description}
                          </p>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Pin className="h-5 w-5" aria-hidden="true" />
            {t('defaultPins.pinnedSpaces')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : pins.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-8 text-center">
              <Pin className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
              <p className="mt-2 text-muted-foreground">
                {t('defaultPins.empty')}
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={pins.map((p) => p.spaceId)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {pins.map((pin) => (
                    <SortablePinItem
                      key={pin.spaceId}
                      pin={pin}
                      onRemove={handleRemove}
                      isRemoving={removeMutation.isPending}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
